import { useTranslations as useTranslations$1 } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { LocaleSwitcher } from '@/components/i18n/LocaleSwitcher';
import { DiscordIcon } from '@/components/icons';
import { DISCORD_INVITE_URL } from '@/lib/constants';
import { DOCS_HOME } from '@/lib/docs';
import { MCP_CLIENT_ROUTE_BASE } from '@/lib/mcpDocsClients';
import { TrackedDiscordLink } from '@/components/analytics/TrackedDiscordLink';
interface FooterLink {
    label: string;
    href: string;
    external?: boolean;
}
interface SocialLink {
    label: string;
    href: string;
    icon: 'instagram' | 'linkedin' | 'discord';
}
function InstagramIcon({ className }: {
    className?: string;
}) {
    return (<svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>);
}
function LinkedInIcon({ className }: {
    className?: string;
}) {
    return (<svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>);
}
const socialIcons = {
    instagram: InstagramIcon,
    linkedin: LinkedInIcon,
    discord: DiscordIcon
} as const;
const FOOTER_LINK_CLASS = 'text-sm text-white/50 transition-colors duration-200 hover:text-white';
const FOOTER_HEADING_CLASS = 'mb-5 text-sm font-medium text-white';
function SocialIcon({ icon }: {
    icon: SocialLink['icon'];
}) {
    const Icon = socialIcons[icon];
    return <Icon className="h-3.5 w-3.5 shrink-0"/>;
}
function FooterNavLink({ link }: {
    link: FooterLink;
}) {
    const label = link.label;
    return link.external ? (// Off-site links open in a new tab; same-site `external` ones (docs) stay in this one.
    <a href={link.href} className={FOOTER_LINK_CLASS} {...(link.href.startsWith('http') ? {
        target: '_blank',
        rel: 'noopener noreferrer'
    } : {})}>
      {label}
    </a>) : (<Link href={link.href} className={FOOTER_LINK_CLASS}>
      {label}
    </Link>);
}
export function Footer() {
    const t = useTranslations$1();
    const footerLinks: {
        product: FooterLink[];
        resources: FooterLink[];
        company: FooterLink[];
        legal: FooterLink[];
        compare: FooterLink[];
        social: SocialLink[];
    } = {
        // Grouped by what a visitor is after: what Martini is (Product), help using it (Resources),
        // who is behind it and who uses it (Company). English-only labels are surfaces with no
        // message-catalog entries yet (feature-PR i18n policy).
        // Docs, the Blender skill, and the MCP connector live on the (app) Learn surface, which is
        // public but not locale-prefixed: `external` renders a plain <a> so the locale-aware Link
        // can't send a non-English visitor to /<locale>/docs (a redirect hop at best).
        product: [
            {
                label: t("5Wwopw"),
                href: '/pricing'
            },
            {
                label: t("UYU6ck"),
                href: '/download'
            },
            {
                label: 'Supported models',
                href: '/supported-models'
            },
            {
                label: 'For AI agents',
                href: '/agents'
            },
            {
                label: 'Martini Bar',
                href: '/bar'
            },
            {
                label: 'MCP connector',
                href: MCP_CLIENT_ROUTE_BASE,
                external: true
            },
            {
                label: 'Blender skill',
                href: '/skills/blender',
                external: true
            }
        ],
        resources: [
            {
                label: t("_6_sPX"),
                href: DOCS_HOME,
                external: true
            },
            {
                label: 'AI film budget calculator',
                href: '/production-calculator'
            },
            // Our directory of MCP servers for the whole AI filmmaking stack (it lists Martini).
            {
                label: 'MCP directory',
                href: 'https://mcp.film',
                external: true
            },
            {
                label: 'FLUX.3 report',
                href: '/models/flux-3'
            },
            {
                label: t("HqRNN8"),
                href: '/support'
            }
        ],
        company: [
            {
                label: 'About',
                href: '/about'
            },
            {
                label: 'Made with Martini',
                href: 'https://madewithmartini.com',
                external: true
            },
            {
                label: 'Podcast',
                href: '/podcast'
            },
            {
                label: 'Press',
                href: '/press'
            },
            {
                label: 'Brand assets',
                href: '/brand-assets'
            },
            // The film-style credits page (team, creative partners, models), not billing credits.
            {
                label: 'Team and credits',
                href: '/credits'
            },
            {
                label: t("zFegDD"),
                href: 'mailto:koh@c47.studio',
                external: true
            }
        ],
        legal: [
            {
                label: t("32rBNK"),
                href: '/terms'
            },
            {
                label: t("vx0nkZ"),
                href: '/privacy'
            },
            {
                label: t("Py189G"),
                href: '/security'
            }
        ],
        // Every comparison listed outright, so they are one click from any page; the full
        // "Martini vs X" is the anchor text search engines and assistants read.
        // Plain literals on purpose (feature-PR i18n policy).
        compare: [
            {
                label: 'Martini vs Higgsfield',
                href: '/compare/higgsfield'
            },
            {
                label: 'Martini vs Magnific',
                href: '/compare/magnific'
            },
            {
                label: 'Martini vs Runway',
                href: '/compare/runway'
            },
            {
                label: 'Martini vs Weavy',
                href: '/compare/weavy'
            }
        ],
        social: [
            {
                // i18n-ignore -- brand name
                label: 'Discord',
                href: DISCORD_INVITE_URL,
                icon: 'discord'
            },
            {
                label: 'Instagram',
                href: 'https://www.instagram.com/martini.film/',
                icon: 'instagram'
            },
            {
                label: 'LinkedIn',
                href: 'https://www.linkedin.com/company/martini-film/',
                icon: 'linkedin'
            }
        ]
    };
    const columns = [
        {
            id: 'product',
            title: 'Product',
            links: footerLinks.product
        },
        {
            id: 'resources',
            title: 'Resources',
            links: footerLinks.resources
        },
        {
            id: 'company',
            title: t("9YazHG"),
            links: footerLinks.company
        },
        {
            id: 'compare',
            title: 'Compare',
            links: footerLinks.compare
        }
    ];
    return (// Pages on the graphite canvas (`data-canvas="graphite"` on their root) get a matching footer, so there is no black seam below them.
    <footer className="relative border-t border-zinc-800/30 bg-black [body:has(#get-started)_&]:border-t-0 [body:has([data-canvas=graphite])_&]:border-white/10 [body:has([data-canvas=graphite])_&]:bg-[#141414]">
      { /* Subtle gradient overlay - hidden on mobile to prevent visual artifacts */ }
      <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-t from-zinc-900/10 via-transparent to-transparent sm:block"/>

      <div className="relative mx-auto max-w-[1200px] px-6 py-16 sm:px-8 lg:px-12 lg:py-20">
        { /* The mark stands alone on the left; the link columns take the rest of the row. */ }
        <div className="lg:flex">
          <div className="mb-12 shrink-0 lg:mb-0 lg:w-[150px]">
            <Link href="/" aria-label="Martini" className="inline-block opacity-90 transition-opacity hover:opacity-100">
              { /* eslint-disable-next-line @next/next/no-img-element -- static brand mark */ }
              <img src="/marketing/martini-mark-white.svg" alt="" className="h-7 w-7"/>
            </Link>
            { /* The line the product is known by in search and in the press; the page itself leads with "workspace". */ }
            <p className="mt-4 max-w-[100px] text-[13px] leading-snug text-white/35">The film set for AI videos.</p>
          </div>
          <div className="flex flex-1 flex-wrap justify-start gap-x-10 gap-y-12 md:justify-between">
            {columns.map((col)=>(<div key={col.id} className="flex-shrink-0" data-footer-column={col.id}>
                <h3 className={FOOTER_HEADING_CLASS}>{col.title}</h3>
                <nav className="flex flex-col gap-3.5" aria-label={`${col.title} links`}>
                  {col.links.map((link)=>(<FooterNavLink key={link.href} link={link}/>))}
                </nav>
              </div>))}
            <div className="flex-shrink-0" data-footer-column="connect">
              <h3 className={FOOTER_HEADING_CLASS}>{t("-vVZ_G")}</h3>
              <div className="flex flex-col gap-3.5">
                {footerLinks.social.map((link)=>link.icon === 'discord' ? (<TrackedDiscordLink key={link.href} source="marketing_footer" className={`${FOOTER_LINK_CLASS} inline-flex items-center gap-2.5`}>
                      <SocialIcon icon={link.icon}/>
                      {link.label}
                    </TrackedDiscordLink>) : (<a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className={`${FOOTER_LINK_CLASS} inline-flex items-center gap-2.5`}>
                      <SocialIcon icon={link.icon}/>
                      {link.label}
                    </a>))}
              </div>
            </div>
          </div>
        </div>

        { /* Bottom row: lines up under the link columns, with the small print kept quiet. */ }
        <div className="mt-16 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center lg:ps-[150px]">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] text-white/35">
            <p>{t("bFPx9p", {
        year: String(new Date().getFullYear())
    })}</p>
            <nav className="flex gap-6" aria-label="Legal links" data-footer-column="legal">
              {footerLinks.legal.map((link)=>(<Link key={link.href} href={link.href} className="transition-colors duration-200 hover:text-white">
                  {link.label}
                </Link>))}
            </nav>
          </div>
          <LocaleSwitcher variant="dropdown" className="w-full max-w-56 sm:w-48"/>
        </div>
      </div>
    </footer>);
}
